import "@/App.css";
import "./styles/modo-demo.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { destinoParaRol } from "./auth/destinos";
import LoginAdmin from "./pages/LoginAdmin";
import LoginChofer from "./pages/LoginChofer";
import LoginLiderVial from "./pages/LoginLiderVial";
import Hub from "./pages/Hub";
import PipasModule from "./pages/PipasModule";
import CapturistaModule from "./pages/CapturistaModule";
import AreasModule from "./pages/AreasModule";
import ReporteModule from "./pages/ReporteModule";
import { Toaster } from "sonner";
import { ChoferMenu, ChoferRuta, ChoferValidar, ChoferResumen, ChoferEvento, ChoferPrioridad } from "./pages/chofer";
import ChoferIntervencion from "./pages/ChoferIntervencion";
import SupervisorDashboard from "./pages/SupervisorDashboard";
import SolicitudesPipaModule from "./pages/SolicitudesPipaModule";
import AlertasModule from "./pages/AlertasModule";
import CoberturasModule from "./pages/CoberturasModule";
import SuspensionesModule from "./pages/SuspensionesModule";
import ReporteDiarioModule from "./pages/ReporteDiarioModule";
import AdminMantenimiento from "./pages/AdminMantenimiento";
import EvidenciaPublica from "./pages/EvidenciaPublica";
import ActividadPublica from "./pages/ActividadPublica";
import MapaIntervenciones from "./pages/MapaIntervenciones";
import AdminImportExcel from "./pages/AdminImportExcel";
import RutasImpresionPage from "./pages/RutasImpresionPage";
import AguaPotablePage from "./pages/AguaPotablePage";
import VialidadesModule from "./pages/VialidadesModule";
import CapturistaAcPage from "./pages/CapturistaAcPage";
import BandejaReportesACPage from "./pages/BandejaReportesACPage";
import CombustiblePage from "./pages/CombustiblePage";
import ControlVehicularModule from "./pages/ControlVehicularModule";
import RegistroUsoPublic from "./pages/RegistroUsoPublic";
import HistorialUnidadPage from "./pages/HistorialUnidadPage";
import ValidarPipas from "./pages/ValidarPipas";
import ResumenEjecutivoPipas from "./pages/ResumenEjecutivoPipas";
import UsoDiarioAdmin from "./pages/UsoDiarioAdmin";
import PuntosPendientesPanel from "./pages/PuntosPendientesPanel";
import PruebaBotPanel from "./pages/PruebaBotPanel";
import LicenciasModule from "./pages/LicenciasModule";
import TrabajosEspecialesModule from "./pages/TrabajosEspecialesModule";
import ReporteTrabajosEspeciales from "./pages/ReporteTrabajosEspeciales";
import PrivacidadRedirect from "./pages/PrivacidadRedirect";
import PwaInstallBanner from "./components/PwaInstallBanner";

function Forbidden() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 px-6" data-testid="forbidden-403">
      <div className="max-w-md w-full text-center bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
        <div className="text-6xl font-bold text-rose-600 mb-2" data-testid="forbidden-code">403</div>
        <h1 className="text-xl font-semibold text-slate-900 mb-1">Acceso denegado</h1>
        <p className="text-sm text-slate-600 mb-6">
          Tu rol <span className="font-mono px-2 py-0.5 bg-slate-100 rounded">{user?.rol || "—"}</span> no tiene permisos para ver esta sección.
        </p>
        <a
          href="/hub"
          className="inline-block px-4 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800"
          data-testid="forbidden-go-hub"
        >
          Volver al inicio
        </a>
      </div>
    </div>
  );
}

function Protected({ children, allow }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen grid place-items-center" data-testid="loading"><div className="text-slate-500">Cargando…</div></div>;
  // Sin token → redirige al login principal.
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  // Director: solo puede ver /reporte-diario. Cualquier otra ruta lo redirige suavemente.
  if (user.rol === "director" && loc.pathname !== "/reporte-diario") return <Navigate to="/reporte-diario" replace />;
  // Capturista AC (Luisa): solo puede ver /capturista-ac. Cualquier otra ruta lo redirige.
  if (user.rol === "capturista_ac" && loc.pathname !== "/capturista-ac") return <Navigate to="/capturista-ac" replace />;
  // Roles módulo Combustible: aterrizan en /combustible y solo se mueven dentro de ese módulo.
  // Excepción (Octavio 18-jun-2026): coordinadora_administrativa y control_vehicular pueden ir a /admin/uso-diario.
  const ROLES_COMBUSTIBLE_PIN = ["capturista_combustible", "jefa_capturistas"];
  if (ROLES_COMBUSTIBLE_PIN.includes(user.rol) && loc.pathname !== "/combustible") return <Navigate to="/combustible" replace />;
  if (user.rol === "coordinadora_administrativa"
      && loc.pathname !== "/combustible"
      && loc.pathname !== "/admin/uso-diario") return <Navigate to="/combustible" replace />;
  if (user.rol === "control_vehicular"
      && !loc.pathname.startsWith("/admin/control-vehicular")
      && loc.pathname !== "/admin/uso-diario")
    return <Navigate to="/admin/control-vehicular" replace />;
  // control_taller (Gelacio, Juan): /supervisor + /admin/control-vehicular + /admin/uso-diario + /admin/licencias + /admin/trabajos-especiales — Octavio 28-ago-2026
  if (user.rol === "control_taller"
      && !loc.pathname.startsWith("/supervisor")
      && !loc.pathname.startsWith("/admin/control-vehicular")
      && !loc.pathname.startsWith("/admin/licencias")
      && !loc.pathname.startsWith("/admin/trabajos-especiales")
      && !loc.pathname.startsWith("/admin/reporte-trabajos-especiales")
      && loc.pathname !== "/admin/uso-diario"
      && loc.pathname !== "/hub")
    return <Navigate to="/supervisor" replace />;
  // capturista_trabajos_especiales — Hub + módulo Fontanería/Herrería + reporte
  if (user.rol === "capturista_trabajos_especiales"
      && !loc.pathname.startsWith("/admin/trabajos-especiales")
      && !loc.pathname.startsWith("/admin/reporte-trabajos-especiales")
      && loc.pathname !== "/hub")
    return <Navigate to="/hub" replace />;
  // Choferes siempre en /chofer (UX: redirección suave, no 403).
  if (user.rol === "chofer" && !loc.pathname.startsWith("/chofer")) return <Navigate to="/chofer" replace />;
  // Rol autenticado pero NO autorizado para esta ruta → 403 explícito (Octavio 20-jun-2026).
  if (allow && !allow.includes(user.rol)) return <Forbidden />;
  return children;
}

function Root() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center"><div className="text-slate-500">Cargando…</div></div>;
  if (user) return <Navigate to={destinoParaRol(user.rol)} replace />;
  return <LoginAdmin />;
}

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors />
      <PwaInstallBanner />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Root />} />
          <Route path="/login" element={<Root />} />
          <Route path="/lider-vial/login" element={<LoginLiderVial />} />
          <Route path="/evidencia/:folio" element={<EvidenciaPublica />} />
          <Route path="/evidencia-evento/:eventoId" element={<EvidenciaPublica />} />
          {/* Actividad pública del día — sin login */}
          <Route path="/actividad" element={<ActividadPublica />} />
          <Route path="/mapa" element={<MapaIntervenciones />} />
          <Route path="/privacidad" element={<PrivacidadRedirect />} />
          {/* Bitácora pública de uso de unidades (sin login) — Octavio 18-jun-2026 */}
          <Route path="/registro-uso" element={<RegistroUsoPublic />} />
          {/* Historial público de taller por unidad (sin login) — Octavio 20-ago-2026 */}
          <Route path="/historial-unidad/:id" element={<HistorialUnidadPage />} />
          {/* Validación de pipas para Saúl (código PIPAS_VALIDACION_CODE) */}
          <Route path="/validar-pipas" element={<ValidarPipas />} />
          {/* Resumen ejecutivo de solo lectura para coordinador_pipas (Rubén) */}
          <Route path="/resumen-ejecutivo" element={<Protected allow={["admin","coordinador_pipas","supervisor_general_pipas","director","jefe"]}><ResumenEjecutivoPipas /></Protected>} />
          <Route path="/admin/uso-diario" element={<Protected allow={["admin","coordinadora_administrativa","control_vehicular","control_taller"]}><UsoDiarioAdmin /></Protected>} />
          <Route path="/admin/puntos-pendientes" element={<Protected allow={["admin","supervisor_general_pipas","supervisor_zona","director","coordinador_pipas"]}><PuntosPendientesPanel /></Protected>} />
          <Route path="/admin/prueba-bot" element={<Protected allow={["admin"]}><PruebaBotPanel /></Protected>} />
          <Route path="/admin/licencias" element={<Protected allow={["admin","control_taller"]}><LicenciasModule /></Protected>} />
          <Route path="/hub" element={<Protected allow={["admin","supervisor_pipas","supervisor_general_pipas","supervisor_areas","capturista","mesa_vial","visor_vial","supervisor_vial","lider_vial","jefe_vial","coordinador_pipas","control_taller","capturista_trabajos_especiales"]}><Hub /></Protected>} />
          <Route path="/pipas" element={<Protected allow={["admin","supervisor_pipas","supervisor_general_pipas","capturista","coordinador_pipas"]}><PipasModule /></Protected>} />
          <Route path="/captura" element={<Protected allow={["admin","capturista"]}><CapturistaModule /></Protected>} />
          <Route path="/areas" element={<Protected allow={["admin","supervisor_areas"]}><AreasModule /></Protected>} />
          <Route path="/reportes" element={<Protected allow={["admin","supervisor_pipas","supervisor_general_pipas","supervisor_areas","capturista","coordinador_pipas"]}><ReporteModule /></Protected>} />
          <Route path="/supervisor" element={<Protected allow={["admin","supervisor_pipas","supervisor_general_pipas","supervisor_areas","capturista","coordinador_pipas","control_taller"]}><SupervisorDashboard /></Protected>} />
          <Route path="/solicitudes-pipa" element={<Protected allow={["admin","supervisor_pipas","supervisor_general_pipas","coordinador_pipas"]}><SolicitudesPipaModule /></Protected>} />
          <Route path="/alertas" element={<Protected allow={["admin","supervisor_pipas","supervisor_general_pipas","coordinador_pipas"]}><AlertasModule /></Protected>} />
          <Route path="/coberturas" element={<Protected allow={["admin","supervisor_general_pipas","coordinador_pipas"]}><CoberturasModule /></Protected>} />
          <Route path="/suspensiones" element={<Protected allow={["admin","supervisor_general_pipas","coordinador_pipas"]}><SuspensionesModule /></Protected>} />
          <Route path="/reporte-diario" element={<Protected allow={["admin","supervisor_general_pipas","director","coordinador_pipas"]}><ReporteDiarioModule /></Protected>} />
          <Route path="/admin/mantenimiento" element={<Protected allow={["admin"]}><AdminMantenimiento /></Protected>} />
          <Route path="/admin/import-excel" element={<Protected allow={["admin"]}><AdminImportExcel /></Protected>} />
          <Route path="/admin/rutas-impresion" element={<Protected allow={["admin","supervisor_general_pipas"]}><RutasImpresionPage /></Protected>} />
          <Route path="/admin/agua-potable" element={<Protected allow={["admin","supervisor_general_pipas"]}><AguaPotablePage /></Protected>} />
          <Route path="/vialidades" element={<Protected allow={["admin","mesa_vial","visor_vial","supervisor_vial","lider_vial","jefe_vial","capturista"]}><VialidadesModule /></Protected>} />
          <Route path="/capturista-ac" element={<Protected allow={["admin","capturista_ac"]}><CapturistaAcPage /></Protected>} />
          <Route path="/bandeja-reportes-ac" element={<Protected allow={["admin","supervisor_general_pipas","supervisor_pipas"]}><BandejaReportesACPage /></Protected>} />
          <Route path="/combustible" element={<Protected allow={["admin","capturista_combustible","jefa_capturistas","coordinadora_administrativa"]}><CombustiblePage /></Protected>} />
          <Route path="/admin/control-vehicular" element={<Protected allow={["admin","control_vehicular","control_taller"]}><ControlVehicularModule /></Protected>} />
          <Route path="/admin/trabajos-especiales" element={<Protected allow={["admin","control_taller","capturista_trabajos_especiales"]}><TrabajosEspecialesModule /></Protected>} />
          <Route path="/admin/reporte-trabajos-especiales" element={<Protected allow={["admin","control_taller","capturista_trabajos_especiales"]}><ReporteTrabajosEspeciales /></Protected>} />
          <Route path="/chofer"          element={<Protected allow={["chofer"]}><ChoferMenu /></Protected>} />
          <Route path="/chofer/ruta"     element={<Protected allow={["chofer"]}><ChoferRuta /></Protected>} />
          <Route path="/chofer/validar/:clave" element={<Protected allow={["chofer"]}><ChoferValidar /></Protected>} />
          <Route path="/chofer/resumen"  element={<Protected allow={["chofer"]}><ChoferResumen /></Protected>} />
          <Route path="/chofer/evento/:tipo" element={<Protected allow={["chofer"]}><ChoferEvento /></Protected>} />
          <Route path="/chofer/intervencion" element={<Protected allow={["chofer"]}><ChoferIntervencion /></Protected>} />
          <Route path="/chofer/prioridad/:id" element={<Protected allow={["chofer"]}><ChoferPrioridad /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
